<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { MirrorsListEditFormSchema } from '@modules/mirrors-list/submodules/edit/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useMirrorsListNamespace } from '@modules/mirrors-list/composables';
import {
  MIRRORS_LIST_JS_MAIN_ROUTE_NAME,
  MIRRORS_LIST_NO_JS_MAIN_ROUTE_NAME,
} from '@modules/mirrors-list/constants';
import { TRANSLATION_GROUP_MIRRORS_EDIT } from '@modules/mirrors-list/submodules/edit/constants';
import { useMirrorsListEditMutation } from '@modules/mirrors-list/submodules/edit/mutations';
import { useMirrorsListEditQuery } from '@modules/mirrors-list/submodules/edit/queries';
import {
  assert,
  isNumber,
} from '@utils/guards';

const route = useRoute();
const { getTranslation } = useI18n();
const { mirrorNamespaceType, isMirrorJsNamespace } = useMirrorsListNamespace();

const id = computed<number | undefined>(() => (
  route.params.id ? Number(route.params.id) : undefined
));

const { data, isLoading: isLoadingItem } = useMirrorsListEditQuery(id);

const {
  mutate: mirrorItemEdit,
  isLoading: isLoadingEdit,
  clearError,
} = useMirrorsListEditMutation();

const backRouteName = computed(() => (
  isMirrorJsNamespace.value ? MIRRORS_LIST_JS_MAIN_ROUTE_NAME : MIRRORS_LIST_NO_JS_MAIN_ROUTE_NAME
));

const headerTitle = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'JS_EDIT_HEADER').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'NO_JS_EDIT_HEADER').value));

const submitBtnLabel = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'JS_EDIT_SAVE').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'NO_JS_EDIT_SAVE').value));

const backBtnLabel = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'JS_EDIT_BACK').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'NO_JS_EDIT_BACK').value));

const domainLabel = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'JS_EDIT_FORM_LINK').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'NO_JS_EDIT_FORM_LINK').value));

const priorityLabel = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'JS_EDIT_FORM_PRIORITY').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'NO_JS_EDIT_FORM_PRIORITY').value));

const enabledLabel = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'JS_EDIT_FORM_ENABLED').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_EDIT, 'NO_JS_EDIT_FORM_ENABLED').value));

const formSchema = computed<FormSchema<MirrorsListEditFormSchema>>(() => ({
  domain: {
    type: EFormInputTypes.TEXT,
    label: domainLabel.value,
    value: data.value?.domain,
    required: true,
  },
  priority: {
    type: EFormInputTypes.NUMBER,
    label: priorityLabel.value,
    value: data.value?.priority,
    required: true,
  },
  enabled: {
    type: EFormInputTypes.CHECKBOX,
    label: enabledLabel.value,
    value: data.value?.enabled,
    required: true,
  },
}));

function handleEdit(formData: MirrorsListEditFormSchema): void {
  assert(isNumber(id.value));
  mirrorItemEdit({
    mirror_type: mirrorNamespaceType.value,
    id: id.value,
    ...formData,
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'MirrorsListEditPage'"
    :title="headerTitle"
    type="form"
  >
    <VLoader
      :loading="isLoadingItem"
      min-height="400px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="submitBtnLabel"
        :is-loading="isLoadingEdit"
        @submit="handleEdit($event)"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VBackButton
            :label="backBtnLabel"
            :to="{ name: backRouteName }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
